/**
 * AppHealthTab — universal health & compliance dashboard for Opsive, VoxLane, RelayCore.
 * Pass `app` = "opsive" | "voxlane" | "relaycore"
 * Reads from GET /api/admin/health/{app}
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle, CheckCircle2, XCircle, Info,
  ShieldAlert, RefreshCw, ChevronDown, ChevronUp,
  ArrowUpRight, Loader2,
} from "lucide-react";

const API = process.env.REACT_APP_API || "https://api.sharpwithit.vip";

const req = (path) =>
  fetch(`${API}/api${path}`, { credentials: "include" }).then((r) => {
    if (!r.ok) throw new Error(`${r.status}`);
    return r.json();
  });

const post = (path, body = {}) =>
  fetch(`${API}/api${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).then((r) => r.json());

// ── Level config ──────────────────────────────────────────────────────────────
const LEVEL = {
  critical: { color: "text-red-400",    bg: "bg-red-500/10 border-red-500/30",    icon: XCircle,        label: "Critical" },
  error:    { color: "text-orange-400", bg: "bg-orange-500/10 border-orange-500/30", icon: AlertTriangle, label: "Error"   },
  warning:  { color: "text-yellow-400", bg: "bg-yellow-500/10 border-yellow-500/30", icon: AlertTriangle, label: "Warning" },
  info:     { color: "text-blue-400",   bg: "bg-blue-500/10 border-blue-500/30",   icon: Info,           label: "Info"    },
};

function LevelBadge({ level }) {
  const cfg = LEVEL[level] || LEVEL.info;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${cfg.bg} ${cfg.color} font-medium`}>
      {level}
    </span>
  );
}

function AlertCard({ alert, onResolve, onEscalate }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const cfg = LEVEL[alert.level] || LEVEL.info;
  const Icon = cfg.icon;

  const resolve = async () => {
    setBusy(true);
    await post(`/admin/alerts/${alert.alert_id}/resolve`);
    onResolve(alert.alert_id);
  };

  const escalate = async () => {
    setBusy(true);
    const res = await post(`/admin/alerts/${alert.alert_id}/escalate`);
    onEscalate(alert.alert_id, res);
    setBusy(false);
  };

  return (
    <div className={`border rounded-xl overflow-hidden ${cfg.bg}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start gap-3 p-4 text-left"
      >
        <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${cfg.color}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <LevelBadge level={alert.level} />
            <span className="text-xs text-gray-500 font-mono">{alert.category}</span>
            <span className="text-xs text-gray-600 ml-auto">
              {new Date(alert.created_at).toLocaleString()}
            </span>
          </div>
          <p className="text-sm text-gray-200 mt-1">{alert.message}</p>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-white/10 pt-3">
          {/* Context JSON */}
          {alert.context && Object.keys(alert.context).length > 0 && (
            <div className="bg-black/30 rounded-lg p-3">
              <p className="text-xs text-gray-500 font-semibold mb-1">Context</p>
              <pre className="text-xs text-gray-300 whitespace-pre-wrap overflow-x-auto">
                {JSON.stringify(alert.context, null, 2)}
              </pre>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              disabled={busy}
              onClick={resolve}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600/20 border border-green-600/30 text-green-400 rounded-lg text-xs hover:bg-green-600/30 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              Mark resolved
            </button>
            {!alert.escalated && (
              <button
                disabled={busy}
                onClick={escalate}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600/20 border border-purple-600/30 text-purple-400 rounded-lg text-xs hover:bg-purple-600/30 disabled:opacity-50"
              >
                <ArrowUpRight className="w-3.5 h-3.5" />
                Escalate to Computer
              </button>
            )}
            {alert.escalated && (
              <span className="text-xs text-purple-400 flex items-center gap-1">
                <ShieldAlert className="w-3.5 h-3.5" /> Escalated
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function EscalationCard({ esc }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-purple-500/30 bg-purple-500/10 rounded-xl overflow-hidden">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-start gap-3 p-4 text-left">
        <ShieldAlert className="w-4 h-4 mt-0.5 text-purple-400 flex-shrink-0" />
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-purple-400 font-medium">Computer Fix Plan</span>
            <span className="text-xs text-gray-500 font-mono">{esc.alert_category}</span>
          </div>
          <p className="text-sm text-gray-200 mt-1">{esc.analysis?.summary}</p>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
      </button>
      {open && esc.analysis && (
        <div className="px-4 pb-4 space-y-3 border-t border-purple-500/20 pt-3">
          <div>
            <p className="text-xs font-semibold text-gray-400 mb-2">Steps to resolve:</p>
            <ol className="space-y-1.5">
              {esc.analysis.steps?.map((step, i) => (
                <li key={i} className="flex gap-2 text-sm text-gray-300">
                  <span className="text-purple-400 font-mono text-xs mt-0.5 flex-shrink-0">{i + 1}.</span>
                  {step}
                </li>
              ))}
            </ol>
          </div>
          {esc.analysis.legal_ref && (
            <p className="text-xs text-gray-500">
              <span className="font-semibold text-gray-400">Legal ref:</span> {esc.analysis.legal_ref}
            </p>
          )}
          {esc.analysis.urgency && (
            <p className="text-xs text-yellow-400">⚠ {esc.analysis.urgency}</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function AppHealthTab({ app }) {
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]     = useState("");
  const [tab, setTab]     = useState("open"); // open | escalations | resolved | scans

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const d = await req(`/admin/health/${app}`);
      setData(d);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [app]);

  useEffect(() => { load(); }, [load]);

  const handleResolve = (alertId) => {
    setData((d) => ({
      ...d,
      open_alerts: d.open_alerts.filter((a) => a.alert_id !== alertId),
      alert_counts: {
        ...d.alert_counts,
      },
    }));
  };

  const handleEscalate = (alertId, res) => {
    setData((d) => ({
      ...d,
      open_alerts: d.open_alerts.map((a) =>
        a.alert_id === alertId ? { ...a, escalated: true, escalation_id: res.escalation_id } : a
      ),
    }));
    load(); // reload to get fresh escalation
  };

  const SCAN_STATUS_COLOR = { ok: "text-green-400", issues: "text-yellow-400", error: "text-red-400" };

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-6 h-6 text-brand animate-spin" />
    </div>
  );

  if (err) return (
    <div className="p-6 text-center text-red-400">
      <p>Failed to load health data: {err}</p>
      <button onClick={load} className="mt-2 text-sm text-gray-400 hover:text-white">Retry</button>
    </div>
  );

  const { open_alerts = [], resolved_alerts = [], alert_counts = {}, scan_results = {}, open_escalations = [], last_scanned_at } = data || {};
  const totalOpen = (alert_counts.critical || 0) + (alert_counts.error || 0) + (alert_counts.warning || 0) + (alert_counts.info || 0);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-brand" />
            Health & Compliance
          </h2>
          {last_scanned_at && (
            <p className="text-xs text-gray-500 mt-0.5">
              Last scan: {new Date(last_scanned_at).toLocaleString()}
            </p>
          )}
        </div>
        <button onClick={load}
          className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white px-3 py-1.5 rounded-lg hover:bg-gray-800">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Summary Pills */}
      <div className="flex flex-wrap gap-3">
        {Object.entries(LEVEL).map(([lvl, cfg]) => {
          const count = alert_counts[lvl] || 0;
          return (
            <div key={lvl} className={`px-3 py-1.5 rounded-lg border text-sm font-medium ${count > 0 ? cfg.bg + " " + cfg.color : "bg-gray-800 border-gray-700 text-gray-500"}`}>
              {count} {cfg.label}
            </div>
          );
        })}
        {open_escalations.length > 0 && (
          <div className="px-3 py-1.5 rounded-lg border bg-purple-500/10 border-purple-500/30 text-purple-400 text-sm font-medium">
            {open_escalations.length} Escalated
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-800 gap-1">
        {[
          { id: "open",        label: `Open Alerts (${totalOpen})` },
          { id: "escalations", label: `Fix Plans (${open_escalations.length})` },
          { id: "scans",       label: `Scan Results (${Object.keys(scan_results).length})` },
          { id: "resolved",    label: `Resolved (${resolved_alerts.length})` },
        ].map(({ id, label }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === id
                ? "border-brand text-brand"
                : "border-transparent text-gray-400 hover:text-white"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === "open" && (
        <div className="space-y-3">
          {open_alerts.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-2" />
              <p className="font-medium text-white">All clear</p>
              <p className="text-sm">No open alerts for {app}</p>
            </div>
          ) : (
            open_alerts.map((a) => (
              <AlertCard key={a.alert_id} alert={a} onResolve={handleResolve} onEscalate={handleEscalate} />
            ))
          )}
        </div>
      )}

      {tab === "escalations" && (
        <div className="space-y-3">
          {open_escalations.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No active escalations.</p>
          ) : (
            open_escalations.map((e) => <EscalationCard key={e.escalation_id} esc={e} />)
          )}
        </div>
      )}

      {tab === "scans" && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 text-left">
                <th className="px-4 py-2.5 text-xs text-gray-500">Scan</th>
                <th className="px-4 py-2.5 text-xs text-gray-500">Status</th>
                <th className="px-4 py-2.5 text-xs text-gray-500">Issues</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(scan_results).map(([name, res]) => (
                <tr key={name} className="border-b border-gray-800">
                  <td className="px-4 py-2.5 font-mono text-xs text-gray-400">{name}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-xs font-medium ${SCAN_STATUS_COLOR[res.status] || "text-gray-400"}`}>
                      {res.status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-400">{res.count ?? res.error ?? "—"}</td>
                </tr>
              ))}
              {Object.keys(scan_results).length === 0 && (
                <tr>
                  <td colSpan={3} className="px-4 py-6 text-center text-gray-500 text-xs">
                    No scan data yet — runs every 5 min.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "resolved" && (
        <div className="space-y-2">
          {resolved_alerts.length === 0 ? (
            <p className="text-gray-500 text-center py-8 text-sm">No resolved alerts.</p>
          ) : (
            resolved_alerts.map((a) => (
              <div key={a.alert_id} className="border border-gray-800 rounded-lg p-3 opacity-60">
                <div className="flex items-center gap-2 flex-wrap">
                  <LevelBadge level={a.level} />
                  <span className="text-xs text-gray-500 font-mono">{a.category}</span>
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-500 ml-auto" />
                </div>
                <p className="text-sm text-gray-400 mt-1">{a.message}</p>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
